import React, { useState } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Card,
  Typography,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  DatePicker,
  TimePicker,
  message,
  Calendar,
  Badge,
  Dropdown,
  Tooltip,
  Divider,
  Avatar,
  Statistic,
  FloatButton,
  Segmented,
  Flex,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  FilterOutlined,
  ReloadOutlined,
  CalendarOutlined,
  TableOutlined,
  UserOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  SearchOutlined,
  MoreOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import dayjs from "dayjs";
import {
  bookingService,
  type CreateBookingRequest,
  type GetBookingsParams,
} from "../services/bookingService";
import type { Booking } from "../types";
import { customerService } from "../services/customerService";
import { courseService } from "../services/courseService";
import { roomService } from "../services/roomService";
import { employeeService } from "../services/employeeService";

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const BookingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useState<GetBookingsParams>({
    page: 1,
    limit: 10,
  });
  const [modal, contextHolder] = Modal.useModal();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(
    dayjs().format("YYYY-MM-DD")
  );
  const [viewMode, setViewMode] = useState<"table" | "calendar">("table");
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState<string>("");
  const queryClient = useQueryClient();

  // Fetch bookings
  const {
    data: bookingsData,
    isLoading,
    refetch,
  } = useQuery(
    ["bookings", searchParams],
    () => bookingService.getBookings(searchParams),
    { keepPreviousData: true }
  );

  // Fetch customers for select
  const { data: customersData } = useQuery(["customers"], () =>
    customerService.getCustomers({ limit: 1000 })
  );

  // Fetch courses for select
  const { data: coursesData } = useQuery(["courses"], () =>
    courseService.getCourses({ limit: 1000 })
  );

  // Fetch rooms for select
  const { data: roomsData } = useQuery(["rooms"], () =>
    roomService.getRooms({ limit: 1000 })
  );

  // Fetch employees
  const { data: employeesData } = useQuery(
    ["employees"],
    () => employeeService.getEmployees({ role: "therapist", status: "active" }),
    { keepPreviousData: true }
  );

  // Fetch bookings by date for calendar
  const { data: dateBookingsData } = useQuery(
    ["bookings", selectedDate],
    () => bookingService.getBookingsByDate(selectedDate),
    { enabled: viewMode === "calendar" }
  );

  // Create/Update booking mutation
  const bookingMutation = useMutation(
    (data: CreateBookingRequest & { id?: string }) => {
      if (data.id) {
        const { id, ...updateData } = data;
        return bookingService.updateBooking(id, updateData);
      }
      return bookingService.createBooking(data);
    },
    {
      onSuccess: () => {
        message.success({
          content: editingBooking
            ? "✅ ແກ້ໄຂການຈອງສຳເລັດ"
            : "✅ ສ້າງການຈອງສຳເລັດ",
          duration: 3,
        });
        setIsModalVisible(false);
        setEditingBooking(null);
        form.resetFields();
        queryClient.invalidateQueries(["bookings"]);
      },
      onError: (error: any) => {
        message.error({
          content: `❌ ${error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ"}`,
          duration: 4,
        });
      },
    }
  );

  // Confirm booking mutation
  const confirmMutation = useMutation(bookingService.confirmBooking, {
    onSuccess: () => {
      message.success({
        content: "✅ ຢືນຢັນການຈອງສຳເລັດ",
        duration: 3,
      });
      queryClient.invalidateQueries(["bookings"]);
    },
    onError: (error: any) => {
      message.error({
        content: `❌ ${error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ"}`,
        duration: 4,
      });
    },
  });

  // Cancel booking mutation
  const cancelMutation = useMutation(
    ({ id, reason }: { id: string; reason: string }) =>
      bookingService.cancelBooking(id, reason),
    {
      onSuccess: () => {
        message.success({
          content: "✅ ຍົກເລິການຈອງສຳເລັດ",
          duration: 3,
        });
        queryClient.invalidateQueries(["bookings"]);
      },
      onError: (error: any) => {
        message.error({
          content: `❌ ${error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ"}`,
          duration: 4,
        });
      },
    }
  );

  const handleEdit = (booking: Booking) => {
    setEditingBooking(booking);
    form.setFieldsValue({
      ...booking,
      customer: booking.customer?._id,
      course: booking.course?._id,
      scheduledDate: dayjs(booking.scheduledDate),
      scheduledTime: dayjs(booking.scheduledTime, "HH:mm"),
      assignedTherapists: booking.assignedTherapists?.map((t) => t._id),
      assignedRoom: booking.assignedRoom?._id,
    });
    setIsModalVisible(true);
  };

  const handleView = (booking: Booking) => {
    Modal.info({
      title: (
        <Text strong style={{ fontSize: 18 }}>
          📋 ລາຍລະອຽດການຈອງ
        </Text>
      ),
      width: 600,
      content: (
        <div style={{ padding: "16px 0" }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card size="small" style={{ backgroundColor: "#f8f9fa" }}>
                <Statistic
                  title="👤 ລູກຄ້າ"
                  value={booking?.customer?.name}
                  valueStyle={{ fontSize: 16 }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" style={{ backgroundColor: "#f8f9fa" }}>
                <Statistic
                  title="💆‍♀️ ບໍລິການ"
                  value={booking?.course?.name}
                  valueStyle={{ fontSize: 16 }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" style={{ backgroundColor: "#f8f9fa" }}>
                <Statistic
                  title="📅 ວັນທີ"
                  value={dayjs(booking.scheduledDate).format("DD/MM/YYYY")}
                  valueStyle={{ fontSize: 16 }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" style={{ backgroundColor: "#f8f9fa" }}>
                <Statistic
                  title="🕐 ເວລາ"
                  value={booking.scheduledTime}
                  valueStyle={{ fontSize: 16 }}
                />
              </Card>
            </Col>
            <Col span={24}>
              <Card size="small" style={{ backgroundColor: "#f8f9fa" }}>
                <Text strong>👩‍⚕️ ໝໍນວດ: </Text>
                <Text>
                  {booking.assignedTherapists?.map((t) => t.name).join(", ") ||
                    "ຍັງບໍ່ໄດ້ກຳນົດ"}
                </Text>
                <br />
                <Text strong>🏠 ຫ້ອງ: </Text>
                <Text>
                  {booking.assignedRoom?.roomNumber || "ຍັງບໍ່ໄດ້ກຳນົດ"}
                </Text>
                {booking.notes && (
                  <>
                    <br />
                    <Text strong>📝 ໝາຍເຫດ: </Text>
                    <Text>{booking.notes}</Text>
                  </>
                )}
              </Card>
            </Col>
          </Row>
        </div>
      ),
      okText: "ປິດ",
    });
  };

  const handleConfirm = (booking: Booking) => {
    modal.confirm({
      title: "✅ ຢືນຢັນການຈອງ",
      content: (
        <div style={{ padding: "16px 0" }}>
          <Text>ຕ້ອງການຢືນຢັນການຈອງແທ້</Text>
          <Text strong style={{ color: "#1890ff", margin: "0 4px" }}>
            "{booking.customer.name}"
          </Text>
          <Text>ຫລື ບໍ?</Text>
          <div
            style={{
              marginTop: 12,
              padding: 12,
              backgroundColor: "#f6ffed",
              borderRadius: 6,
            }}
          >
            <Text type="secondary">
              📅 {dayjs(booking.scheduledDate).format("DD/MM/YYYY")} ເວລາ{" "}
              {booking.scheduledTime}
            </Text>
          </div>
        </div>
      ),
      okText: "ຢືນ",
      cancelText: "ຍົກເລິກ",
      okButtonProps: { type: "primary" },
      onOk: () => confirmMutation.mutate(booking._id),
    });
  };

  const handleCancel = (booking: Booking) => {
    let cancelReason = "";
    modal.confirm({
      title: "🚫 ຍົກເລິກການຈອງ",
      content: (
        <div style={{ padding: "16px 0" }}>
          <Text>ຕ້ອງການຍົກເລິກການຈອງຂອງ</Text>
          <Text strong style={{ color: "#ff4d4f", margin: "0 4px" }}>
            "{booking.customer.name}"
          </Text>
          <Text>ຫຼື ບໍ?</Text>
          <div style={{ marginTop: 16 }}>
            <Text strong>ເຫດຜົນການຍົກເລິກ:</Text>
            <Input.TextArea
              placeholder="ກະລຸນາປ້ອນເຫດຜົນການຍົກເລິກ..."
              rows={3}
              style={{ marginTop: 8 }}
              onChange={(e) => {
                cancelReason = e.target.value;
              }}
            />
          </div>
        </div>
      ),
      okText: "ຍົກເລິກການຈອງ",
      okType: "danger",
      cancelText: "ບໍ່ຍົກເລິກ",
      okButtonProps: { danger: true },
      onOk: () =>
        cancelMutation.mutate({
          id: booking._id,
          reason: cancelReason || "ຍົກເລິກໂດຍພະນັກງານ",
        }),
    });
  };

  const handleSubmit = (values: any) => {
    const data = {
      ...values,
      scheduledDate: values.scheduledDate.format("YYYY-MM-DD"),
      scheduledTime: values.scheduledTime.format("HH:mm"),
      ...(editingBooking && { id: editingBooking._id }),
    };
    bookingMutation.mutate(data);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setSearchParams((prev) => ({ ...prev, search: value, page: 1 }));
  };

const getActionMenu = (booking: Booking) => ({
  items: [
    {
      key: "view",
      label: "ເບິ່ງລາຍລະອຽດ",
      icon: <EyeOutlined />,
    },
    ...(booking.status === "pending"
      ? [
          {
            key: "confirm",
            label: "ຢືນຢັນການຈອງ",
            icon: <CheckOutlined />,
          },
        ]
      : []),
    ...(["pending", "confirmed"].includes(booking.status)
      ? [
          {
            key: "edit",
            label: "ແກ້ໄຂຂໍ້ມູນ",
            icon: <EditOutlined />,
          },
          {
            type: "divider" as const,
          },
          {
            key: "cancel",
            label: "ຍົກເລິກການຈອງ",
            icon: <DeleteOutlined />,
            danger: true,
          },
        ]
      : []),
  ],
});

  const columns = [
    {
      title: "📅 ວັນທີ & ເວລາ",
      key: "datetime",
      render: (_: any, record: Booking) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1890ff" }}>
            {dayjs(record.scheduledDate).format("DD/MM/YYYY")}
          </div>
          <div style={{ fontSize: 12, color: "#666" }}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            {record.scheduledTime}
          </div>
        </div>
      ),
      width: 140,
      sorter: true,
    },
    {
      title: "👤 ລູກຄ້າ",
      key: "customer",
      render: (_: any, record: Booking) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar
            size={32}
            icon={<UserOutlined />}
            style={{ backgroundColor: "#87d068" }}
          />
          <div>
            <div style={{ fontWeight: 500 }}>{record?.customer?.name}</div>
            <div style={{ fontSize: 12, color: "#666" }}>
              📱 {record?.customer?.phone || "ບໍ່ລະບຸ"}
            </div>
          </div>
        </div>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="ຄົ້ນຫາຊື່ລູກຄ້າ"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              ຄົ້ນຫານ
            </Button>
            <Button onClick={() => clearFilters?.()} size="small">
              reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
    },
    {
      title: "💆‍♀️ ບໍລິການ",
      key: "service",
      render: (_: any, record: Booking) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.course.name}</div>
          <div style={{ fontSize: 12, color: "#666" }}>
            ⏱️ {record.duration} ນາທີ
          </div>
        </div>
      ),
    },
    {
      title: "🏠 ຫ້ອງ & ນັກບຳບັດ",
      key: "assignment",
      render: (_: any, record: Booking) => (
        <div>
          <div style={{ marginBottom: 4 }}>
            <EnvironmentOutlined style={{ marginRight: 4, color: "#1890ff" }} />
            <Text strong>{record.assignedRoom?.roomNumber || "ບໍ່ລະບຸ"}</Text>
          </div>
          <div style={{ fontSize: 12 }}>
            👩‍⚕️{" "}
            {record.assignedTherapists?.map((t) => t.name).join(", ") ||
              "ບໍ່ລະບຸ"}
          </div>
        </div>
      ),
      width: 200,
    },
    {
      title: "📊 ສະຖານະ",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const statusConfig = {
          pending: {
            color: "orange",
            text: "⏳ ຖ້າຢືນຢັນ",
            style: { backgroundColor: "#fff7e6", borderColor: "#ffa940" },
          },
          confirmed: {
            color: "blue",
            text: "✅ ຢືນຢັນແລ້ວ",
            style: { backgroundColor: "#e6f7ff", borderColor: "#40a9ff" },
          },
          in_progress: {
            color: "green",
            text: "🔄 ກຳລັງດຳເນີນການ",
            style: { backgroundColor: "#f6ffed", borderColor: "#73d13d" },
          },
          completed: {
            color: "default",
            text: "✅ ສຳເລັດ",
            style: { backgroundColor: "#f5f5f5", borderColor: "#d9d9d9" },
          },
          cancelled: {
            color: "red",
            text: "❌ ຍົກເລິກ",
            style: { backgroundColor: "#fff2f0", borderColor: "#ff7875" },
          },
          no_show: {
            color: "volcano",
            text: "👻 ບໍ່ມາຕາມນັດ",
            style: { backgroundColor: "#fff1f0", borderColor: "#ff9c6e" },
          },
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return (
          <Tag
            style={{
              ...config.style,
              fontWeight: 500,
              padding: "4px 8px",
              borderRadius: 6,
            }}
          >
            {config.text}
          </Tag>
        );
      },
      width: 140,
      filters: [
        { text: "⏳ ຖ້າຢືນຢັນ", value: "pending" },
        { text: "✅ ຢືນຢັນແລ້ວ", value: "confirmed" },
        { text: "🔄 ກຳລັງດຳເນີນການ", value: "in_progress" },
        { text: "✅ ສຳເລັດ", value: "completed" },
        { text: "❌ ຍົກເລິກ", value: "cancelled" },
        { text: "👻 ບໍ່ມາຕາມນັດ", value: "no_show" },
      ],
    },
    {
      title: "⚙️ ຈັດການ",
      key: "actions",
      render: (_: any, record: Booking) => (
        <div style={{ display: "flex", gap: 8 }}>
          {record.status === "pending" && (
            <Tooltip title="ຢືນຢັນການຈອງ">
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => handleConfirm(record)}
                style={{ borderRadius: 6 }}
              />
            </Tooltip>
          )}
          <Dropdown
            menu={{
              items: getActionMenu(record).items,
              onClick: ({ key }) => handleMenuClick(key, record), // ✅ เพิ่ม onClick ตรงนี้
            }}
            trigger={["click"]}
          >
            <Button
              size="small"
              icon={<MoreOutlined />}
              style={{ borderRadius: 6 }}
            />
          </Dropdown>
        </div>
      ),
      width: 120,
    },
  ];

  const handleMenuClick = (key: string, booking: Booking) => {
    switch (key) {
      case "view":
        handleView(booking);
        break;
      case "confirm":
        handleConfirm(booking);
        break;
      case "edit":
        handleEdit(booking);
        break;
      case "cancel":
        handleCancel(booking);
        break;
      default:
        break;
    }
  };

  const getListData = (value: dayjs.Dayjs) => {
    const dateStr = value.format("YYYY-MM-DD");
    const bookings =
      dateBookingsData?.data.filter(
        (booking: Booking) =>
          dayjs(booking.scheduledDate).format("YYYY-MM-DD") === dateStr
      ) || [];

    return bookings.map((booking: Booking) => ({
      type:
        booking.status === "confirmed"
          ? "success"
          : booking.status === "pending"
          ? "warning"
          : "error",
      content: `${booking.scheduledTime} - ${booking.customer.name}`,
    }));
  };

  const dateCellRender = (value: dayjs.Dayjs) => {
    const listData = getListData(value);
    return (
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {listData.map((item: any, index: number) => (
          <li key={index} style={{ marginBottom: 2 }}>
            <Badge
              status={item.type as any}
              text={<Text style={{ fontSize: 11 }}>{item.content}</Text>}
            />
          </li>
        ))}
      </ul>
    );
  };

  // Calculate statistics
  const totalBookings = bookingsData?.data.pagination.total || 0;
  const pendingBookings =
    bookingsData?.data.bookings?.filter((b: Booking) => b.status === "pending")
      .length || 0;
  const confirmedBookings =
    bookingsData?.data.bookings?.filter(
      (b: Booking) => b.status === "confirmed"
    ).length || 0;
  const todayBookings =
    bookingsData?.data.bookings?.filter(
      (b: Booking) =>
        dayjs(b.scheduledDate).format("YYYY-MM-DD") ===
        dayjs().format("YYYY-MM-DD")
    ).length || 0;

  return (
    <div style={{ minHeight: "100vh" }}>
      {/* Header Section */}
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div>
            <Title level={2} style={{ margin: 0, color: "#1890ff" }}>
              📋 ຈັດການຈອງ
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              ລະບົບຈັດການຈອງ ແລະ ນັດໝາຍລູກຄ້າ
            </Text>
          </div>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
            style={{
              borderRadius: 8,
              background: "linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)",
              border: "none",
              height: 48,
              paddingLeft: 24,
              paddingRight: 24,
              fontWeight: 600,
            }}
          >
            ເພີ່ມການຈອງໃໝ່
          </Button>
        </div>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={6}>
            <Card
              style={{
                borderRadius: 12,
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="📊 ການຈອງທັງໝົດ"
                value={totalBookings}
                valueStyle={{ color: "#1890ff", fontSize: 24, fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card
              style={{
                borderRadius: 12,
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="⏳ ຖ້າຢືນຢັນ"
                value={pendingBookings}
                valueStyle={{ color: "#fa8c16", fontSize: 24, fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card
              style={{
                borderRadius: 12,
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="✅ ຢືນຢັນແລ້ວ"
                value={confirmedBookings}
                valueStyle={{ color: "#52c41a", fontSize: 24, fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card
              style={{
                borderRadius: 12,
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="📅 ມື້ນີ້"
                value={todayBookings}
                valueStyle={{ color: "#722ed1", fontSize: 24, fontWeight: 600 }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* Main Content Card */}
      <Card
        style={{
          borderRadius: 16,
          border: "none",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        {/* Controls Section */}
        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} lg={8}>
              <Flex gap={8}>
                <Segmented
                  value={viewMode}
                  onChange={(value: any) => setViewMode(value)}
                  options={[
                    {
                      label: (
                        <div style={{ padding: "4px 8px" }}>
                          <TableOutlined style={{ marginRight: 8 }} />
                          ຕາຕະລາງ
                        </div>
                      ),
                      value: "table",
                    },
                    {
                      label: (
                        <div style={{ padding: "4px 8px" }}>
                          <CalendarOutlined style={{ marginRight: 8 }} />
                          ປະຕິທິນ
                        </div>
                      ),
                      value: "calendar",
                    },
                  ]}
                  style={{ backgroundColor: "#f0f2f5" }}
                />
                <Tooltip title="ຮີເຟຣຂໍ້ມູນ">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => refetch()}
                    style={{ borderRadius: 8 }}
                  />
                </Tooltip>
              </Flex>
            </Col>
            <Col xs={24} lg={16}>
              <Row gutter={[8, 8]}>
                <Col xs={24} sm={8}>
                  <Search
                    placeholder="🔍 ຄົ້ນຫາລູກຄ້າ, ບໍລິການ..."
                    allowClear
                    value={searchText ?? ""}
                    onSearch={handleSearch}
                    style={{
                      borderRadius: 8,
                    }}
                    size="middle"
                  />
                </Col>
                <Col xs={24} sm={6}>
                  <DatePicker
                    value={dayjs(selectedDate)}
                    onChange={(date) =>
                      setSelectedDate(date?.format("YYYY-MM-DD") || "")
                    }
                    style={{ width: "100%", borderRadius: 8 }}
                    placeholder="📅 ເລືອກວັນທີ"
                    size="middle"
                  />
                </Col>
                <Col xs={24} sm={6}>
                  <Select
                    placeholder="📊 ເບິ່ງຕາມສະຖານະ"
                    allowClear
                    style={{ width: "100%", borderRadius: 8 }}
                    size="middle"
                    onChange={(status) =>
                      setSearchParams((prev) => ({ ...prev, status, page: 1 }))
                    }
                  >
                    <Option value="pending">⏳ ຖ້າຢືນຢັນ</Option>
                    <Option value="confirmed">✅ ຢືນຢັນແລ້ວ</Option>
                    <Option value="in_progress">🔄 ກຳລັງດຳເນີນການ</Option>
                    <Option value="completed">✅ ສຳເລັດ</Option>
                    <Option value="cancelled">❌ ຍົກເລິກ</Option>
                  </Select>
                </Col>
                <Col xs={24} sm={4}>
                  <Tooltip title="ຕົວກອງຂໍ້ມູນ">
                    <Button
                      icon={<FilterOutlined />}
                      style={{ width: "100%", borderRadius: 8 }}
                      size="middle"
                    >
                      Filter
                    </Button>
                  </Tooltip>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>

        <Divider style={{ margin: "24px 0" }} />

        {/* Content Section */}
        {viewMode === "table" ? (
          <Table
            columns={columns}
            dataSource={bookingsData?.data?.bookings}
            rowKey="_id"
            loading={isLoading}
            scroll={{ x: 1200 }}
            style={{
              borderRadius: 12,
              overflow: "hidden",
            }}
            rowClassName={(record: Booking) => {
              if (record.status === "pending") return "row-pending";
              if (record.status === "confirmed") return "row-confirmed";
              return "";
            }}
            pagination={{
              current: searchParams.page,
              pageSize: searchParams.limit,
              total: bookingsData?.data.pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `📋 ສະແດງ ${range[0]}-${range[1]} ຈາກ ${total} ລາຍການ`,
              onChange: (page, pageSize) =>
                setSearchParams((prev) => ({ ...prev, page, limit: pageSize })),
              style: { marginTop: 24 },
            }}
          />
        ) : (
          <div
            style={{
              borderRadius: 12,
              overflow: "hidden",
              backgroundColor: "#fafafa",
            }}
          >
            <Calendar
              dateCellRender={dateCellRender}
              value={dayjs(selectedDate)}
              onSelect={(date) => setSelectedDate(date.format("YYYY-MM-DD"))}
              style={{ padding: 16 }}
            />
          </div>
        )}
      </Card>

      {/* Floating Action Button */}
      <FloatButton
        icon={<PlusOutlined />}
        type="primary"
        style={{
          right: 24,
          bottom: 24,
          width: 56,
          height: 56,
        }}
        onClick={() => setIsModalVisible(true)}
        tooltip="ເພີ່ມລາຍການຈອງໃໝ່"
      />

      {contextHolder}

      {/* Add/Edit Booking Modal */}
      <Modal
        title={
          <div style={{ textAlign: "center", padding: "18px 0" }}>
            <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
              {editingBooking ? "✏️ ແກ້ໄຂການຈອງ" : "➕ ເພີ່ມການຈອງໃໝ່"}
            </Title>
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingBooking(null);
          form.resetFields();
        }}
        footer={null}
        width={700}
        style={{
          top: 20,
        }}
        styles={{
          content: {
            borderRadius: 16,
            padding: 0,
          },
        }}
      >
        <div style={{ padding: "24px 24px 24px 24px" }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark="optional"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="customer"
                  label={<Text strong>👤 ລູກຄ້າ</Text>}
                  rules={[{ required: true, message: "ກະລຸນາເລືອກລູກຄ້າ" }]}
                >
                  <Select
                    showSearch
                    placeholder="🔍 ເລືອກລູກຄ້າ"
                    size="large"
                    style={{ borderRadius: 8 }}
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {customersData?.data.customers?.map((customer: any) => (
                      <Option key={customer._id} value={customer._id}>
                        {customer.name} ({customer.phone})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="course"
                  label={<Text strong>💆‍♀️ ບໍລິການ</Text>}
                  rules={[{ required: true, message: "ກະລຸນາເລືອກບໍລິການ" }]}
                >
                  <Select
                    placeholder="🔍 ເລືອກບໍລິການ"
                    size="large"
                    style={{ borderRadius: 8 }}
                  >
                    {coursesData?.data.courses?.map((course: any) => (
                      <Option key={course._id} value={course._id}>
                        {course.name} ({course.duration} ນາທີ)
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="scheduledDate"
                  label={<Text strong>📅 ວັນທີນັດໝາຍ</Text>}
                  rules={[{ required: true, message: "ກະລຸນາເລືອກວັນທີ" }]}
                >
                  <DatePicker
                    style={{ width: "100%", borderRadius: 8 }}
                    size="large"
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="scheduledTime"
                  label={<Text strong>🕐 ເວລານັດໝາຍ</Text>}
                  rules={[{ required: true, message: "ກະລຸນາເລືອກເວລາ" }]}
                >
                  <TimePicker
                    format="HH:mm"
                    style={{ width: "100%", borderRadius: 8 }}
                    size="large"
                    minuteStep={30}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="assignedTherapists"
                  label={<Text strong>👩‍⚕️ ໝໍນວດ</Text>}
                >
                  <Select
                    mode="multiple"
                    placeholder="🔍 ເລືອກໝໍນວດ"
                    size="large"
                    style={{ borderRadius: 8 }}
                  >
                    {employeesData?.data.employees?.map((emp: any) => (
                      <Option key={emp._id} value={emp._id}>
                        {emp.name} ({emp.employeeId})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="assignedRoom"
                  label={<Text strong>🏠 ຫ້ອງ</Text>}
                >
                  <Select
                    placeholder="🔍 ເລືອກຫ້ອງ"
                    allowClear
                    size="large"
                    style={{ borderRadius: 8 }}
                  >
                    {roomsData?.data.rooms?.map((room: any) => (
                      <Option key={room._id} value={room._id}>
                        {room.roomName} ({room.roomNumber})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="notes" label={<Text strong>📝 ໝາຍເຫດ</Text>}>
              <Input.TextArea
                rows={3}
                placeholder="ໝາຍເຫດ..."
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, paddingTop: 16 }}>
              <Flex justify="center" gap={12}>
                <Button
                  size="large"
                  onClick={() => {
                    setIsModalVisible(false);
                    setEditingBooking(null);
                    form.resetFields();
                  }}
                  style={{
                    borderRadius: 8,
                    paddingLeft: 32,
                    paddingRight: 32,
                  }}
                >
                  ຍົກເລິກ
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={bookingMutation.isLoading}
                  style={{
                    borderRadius: 8,
                    background:
                      "linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)",
                    border: "none",
                    paddingLeft: 32,
                    paddingRight: 32,
                    fontWeight: 600,
                  }}
                >
                  {editingBooking ? "💾 ແກ້ໄຂ" : "➕ ເພີ່ມ"}
                </Button>
              </Flex>
            </Form.Item>
          </Form>
        </div>
      </Modal>

      {/* Custom CSS for row styling */}
      <style>{`
        .row-pending {
          background-color: #fff7e6 !important;
        }
        .row-confirmed {
          background-color: #e6f7ff !important;
        }
        .ant-table-tbody > tr:hover.row-pending > td {
          background-color: #ffe7ba !important;
        }
        .ant-table-tbody > tr:hover.row-confirmed > td {
          background-color: #bae7ff !important;
        }
      `}</style>
    </div>
  );
};

export default BookingsPage;
